// Client (staff side, one engagement): every request, program question and follow-up with the client as a thread, with the
// engine's status and every act through the inbox (ThreadWorkspace). New threads start as drafts — a request (pbc.request), a
// program question from a procedure (question.draft with the procedure: its program row becomes the source) or in the person's
// own words — and reach the client only when a person sends them.
import { useState } from "react";
import { ActionDrawer, type ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { Button, LoadState, Modal, StatusPill, inputCls } from "../../components";
import { useLoad } from "../../lib/hooks";
import { ThreadWorkspace } from "../client/ThreadWorkspace";
import { useEngagement } from "./Layout";

export function ClientPage() {
  const { id, model } = useEngagement();
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  const [pick, setPick] = useState(false);
  return (
    <div className="space-y-3">
      <ThreadWorkspace engagements={[{ id, name: model.header.clientName }]} showClient={false} toolbar={
        <>
          <Button size="sm" onClick={() => setIntent({ command: "pbc.request", engagementId: id, title: "Draft a client request" })}>Draft a request</Button>
          <Button size="sm" onClick={() => setPick(true)}>Question from a program row</Button>
          <Button size="sm" onClick={() => setIntent({ command: "question.draft", engagementId: id, title: "Draft a question in your own words", hide: ["source", "procedure"] })}>Draft a question</Button>
        </>
      } />
      <ProgramRowPicker open={pick} engagementId={id} onClose={() => setPick(false)}
        onPick={(p) => { setPick(false); setIntent({ command: "question.draft", engagementId: id, fixed: { procedure: p.id }, initial: { workpaper: p.workpaper }, title: `Draft a question from procedure ${p.id}`, hide: ["source"] }); }} />
      <ActionDrawer intent={intent} onClose={() => setIntent(null)} />
    </div>
  );
}

/** Open procedures of the engagement (engine read model) to draft a program question from; the engine takes the row's text. */
function ProgramRowPicker({ open, engagementId, onClose, onPick }: { open: boolean; engagementId: string; onClose: () => void; onPick: (p: { id: number; workpaper: string }) => void }) {
  const { ds } = useApp();
  const procs = useLoad(() => (open ? ds.table(engagementId, "procedures") : Promise.resolve([])), [ds, engagementId, open]);
  const [q, setQ] = useState("");
  return (
    <Modal open={open} onClose={onClose} label="Choose a program row">
      <div className="flex min-h-0 flex-col gap-3 p-4">
        <h2 className="text-base font-semibold">Draft a question from a program row</h2>
        <label className="sr-only" htmlFor="proc-search">Search procedures</label>
        <input id="proc-search" className={inputCls} placeholder="Search procedures or workpapers" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="min-h-0 overflow-y-auto">
          <LoadState state={procs}>
            {(ps) => {
              const list = ps.filter((p) => p.status === "open" && (!q.trim() || `${p.workpaper} ${p.text}`.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 60);
              return list.length === 0 ? <p className="text-sm text-muted">No open procedure matches.</p> : (
                <ul className="divide-y divide-line rounded border border-line">
                  {list.map((p) => (
                    <li key={p.id}>
                      <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-sunken" onClick={() => onPick({ id: Number(p.id), workpaper: p.workpaper })}>
                        <span className="flex items-center gap-2 text-xs text-muted"><StatusPill state={p.status} /><span className="font-mono">{p.workpaper}</span> procedure {p.id}</span>
                        <span className="mt-0.5 line-clamp-2 block">{p.text}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              );
            }}
          </LoadState>
        </div>
        <div className="flex justify-end"><Button onClick={onClose}>Close</Button></div>
      </div>
    </Modal>
  );
}
